import * as React from 'react';
import { useEffect } from 'react';
import {
  SubmitErrorHandler,
  SubmitHandler
} from 'react-hook-form/dist/types/form';
import {
  BehoerdenpostfachFormProps,
  FitConnectFormProps,
  ZustellwegSubFormProps
} from './Zustellweg.types';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../shared/notification/useNotification';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache,
  invalidateServiceTemplatesCache
} from '../../api/govos-api';
import { GovOsResult, NewDeliverService } from '../../api/govos.types';

export function useZustellwegForm({
  onSuccess,
  primaryButtonClickCount,
  setPrimaryButtonClickCount,
  isParentFormValid,
  serviceIndex,
  startAppId,
  processId,
  templateId,
  parentState,
  handleSubmit,
  shouldSaveAsTemplate,
  newTemplateName
}: ZustellwegSubFormProps & {
  handleSubmit: (
    onValid: SubmitHandler<
      Partial<FitConnectFormProps & BehoerdenpostfachFormProps>
    >,
    onInvalid?: SubmitErrorHandler<
      Partial<FitConnectFormProps & BehoerdenpostfachFormProps>
    >
  ) => (e?: React.BaseSyntheticEvent) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();

  const isTemplateMode = templateId != null;

  const { mutate: createTemplate } = useMutation({
    mutationFn: async (configuration: string) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(GovosApiRoutes.serviceTemplates(startAppId), {
            name: newTemplateName,
            serviceType: 'delivery',
            configuration
          }),
        {
          successMessage: t('api.saveAsTemplate.successMessage'),
          errorMessage: t('api.saveAsTemplate.errorMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
    }
  });

  const updateZustellwegRequest = isTemplateMode
    ? (payload: NewDeliverService) =>
        govosClient.put(
          GovosApiRoutes.serviceTemplate(startAppId, templateId),
          { configuration: JSON.stringify(payload) }
        )
    : serviceIndex === -1
      ? (payload: NewDeliverService) =>
          govosClient.post(
            GovosApiRoutes.deliveryService(startAppId, processId!),
            payload
          )
      : (payload: NewDeliverService) =>
          govosClient.put<GovOsResult<unknown>>(
            GovosApiRoutes.deliveryService(startAppId, processId!),
            payload
          );

  const { mutateAsync: updateTransportConfig } = useMutation({
    mutationFn: (payload: NewDeliverService) =>
      notifyUserFromRequest(() => updateZustellwegRequest(payload), {
        successMessage: t('EditTransportConfigModal.successMessage'),
        errorMessage: t('EditTransportConfigModal.errorMessage'),
        loadingMessage: t('EditTransportConfigModal.loadingMessage')
      }),
    onSuccess: (_, payload) => {
      if (isTemplateMode) {
        void queryClient.invalidateQueries({
          predicate: invalidateServiceTemplatesCache()
        });
      } else {
        void queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
        });
        if (shouldSaveAsTemplate && newTemplateName?.trim()) {
          createTemplate(JSON.stringify(payload));
        }
      }
      onSuccess?.();
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!isParentFormValid) {
      setPrimaryButtonClickCount(0);
      return;
    }

    if (data.serviceId) {
      const payload: NewDeliverService = data.destinationid
        ? {
            serviceId: data.serviceId,
            serviceIndex: serviceIndex,
            serviceCode: parentState.selectedZustellwegType,
            params: { destinationid: data.destinationid }
          }
        : {
            serviceId: data.serviceId,
            serviceIndex: serviceIndex,
            serviceCode: parentState.selectedZustellwegType
          };
      await updateTransportConfig(payload);
    }
  });

  useEffect(() => {
    if (primaryButtonClickCount > 0) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryButtonClickCount, isParentFormValid]);
}
